import Head from "next/head"
import DefaultLayout from "@/components/DefaultLayout"
import SliderBarLeft from "@/components/SliderBarLeft"
import SliderBarRight from "@/components/SliderBarRight"
import ShopInfo from "@/components/ShopInfo"
import styles from "@/styles/zippay.module.scss"
import Image from 'next/image'
import { UpOutlined } from '@ant-design/icons';

import getConfig from 'next/config'
const { publicRuntimeConfig } = getConfig()
const imageDomain = publicRuntimeConfig.imageDomain
export default function Zippay() {

  // 回到顶部
  const backToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // 添加平滑滚动效果
    })
  }

  interface QuestionItem {
    question: string;
    answer: React.ReactNode;
  }

  const questionList = [
    {
      question: `How can I get a zipPay account?`,
      answer: 
        <div className={styles['answer-desc']}>
          You can create an account by using your Facebook, PayPal or email details during checkout at Crazy Sales.<br />
          Sign-up takes only seconds and (subject to approval) you can complete your purchase and pay for it later. You can continue to use your zipPay account anywhere that accepts zipPay.<br />
          Important: you have to use your own mobile number and email/social media account during the signup process or your application might be negatively affected! Also in case you were wondering: you can only have one (1, uno, einen, less than 2 larger than 0) zipPay account at a time.
        </div>
    },
    {
      question: 'Who can apply for zipPay?',
      answer: 
        <div className={styles['answer-desc']}>
          Any Australian citizen or permanent resident, who is 18 years or older is eligible for a zipPay application. A good credit history is beneficial and you need to apply with your own phone number and social account.<br />
          If you don‘t own a mobile phone number or a Facebook or Paypal account, that means unfortunately you won‘t be able to apply.<br />
          In the application process we look at things like your credit score, income stability and if you can pay back the credit. We also check your identity (via Facebook or Paypal) to make sure you are who you say you are. We can’t specifically tell you the exact things we look at, but we hope that gives you an idea on what we base our decisions on.
        </div>
    },
    {
      question: 'What are the different types of accounts?',
      answer: 
        <div className={styles['answer-desc']}>
          We offer zipPay accounts with 3 different types of credit limits:<br />
          •$250<br />
          •$500<br />
          •$1000<br />
          All accounts exactly the same except for the differentiation in credit. Our algorithm will automatically decide which maximum limit you will be eligible for based on our minimum criteria evaluated during the application process.
        </div>
    },
    {
      question: 'How long do I have to send the item back? What if I miss the deadline?',
      answer: 
        <div className={styles['answer-desc']}>
          Notification is required within 30 days of the purchase date.<br />
          The product must be returned to us within 30 calendar days from the approved date.<br />
          Where the 30 Day Change-of-Mind period has elapsed and where Customer Service has determined that on the facts you have provided, a claim may be granted.<br></br>
        </div>
    },
    {
      question: 'Fees and charges',
      answer: 
        <div className={styles['answer-desc']}>
          There are no setup costs and there&apos;s no interest ever with zipPay. zipPay allows you to buy now and pay later with flexible repayment options.<br />
          At the end of each month you can choose to either:<br />
          Pay off your previous months statement balance in full and you’ll avoid paying a $7.95 monthly account fee.<br />
          or<br />
          If you need more time to pay, make a payment that suits your budget (a minimum repayment of at least $40 per month is required). A $7.95 monthly account fee will be added to your account for any month you have an outstanding balance, regardless of how many purchases you make.
        </div>
    },
    {
      question: `How do repayments work?`,
      answer: 
        <div className={styles['answer-desc']}>
          Payments start the month after your first purchase. Payments are taken automatically via direct debit - you don&apos;t need to manually transfer money unless you would like to pay down your balance faster, and it doesn&apos;t replace your scheduled payment run! Payments are taken on the last day of the month or however you set up your schedule in your wallet. You can set up your repayments to fall monthly, fortnightly or weekly at whatever amount suits (provided this is greater than the monthly minimum of $40). If you decide not to set up an individual schedule, we will take repayments automatically at the end of the next month even.<br />
          You can also make additional payments whenever you like via card or BPay. Again, please be aware that your scheduled payment will still be processed even if you make an additional payment outside of schedule! If your payment fails, we will send you a notification. Simply put some money into your nominated payment method and we will attempt to reprocess payments the following day.
        </div>
    },
    {
      question: 'When is my payment usually due?',
      answer: 
        <div className={styles['answer-desc']}>
          Payment for your purchases in one calendar month are not due in full until the last day of the calendar month following. For example, for all purchases made in January, a statement will be issued February 1, with the account balance due for payment on the last day of February.<br />
          However, if you feel like you&apos;d like to make a payment earlier in that month, you can change the schedule to better suit your needs.
        </div>
    },
    {
      question: 'How do I manage my repayment schedule?',
      answer: 
        <div className={styles['answer-desc']}>
          Once you&apos;ve made your first order, your default repayment schedule is set to automatically process $40 at the end of the next month, and every month after that until the balance is 0.<br />
          However, you can manually reschedule how much is deducted and how often using the &quot;Scheduled Payments&quot; settings screen accessed from your digital account.<br />
          Please note that the automatically scheduled transaction will continue to process, even if you make a one off repayment during the month.<br />
          To help you keep track of your finances, your monthly statement provides a summary of your zipPay purchases and payments for the past month.
        </div>
    },
    {
      question: `What is the minimum repayment required each month?`,
      answer: 
        <div className={styles['answer-desc']}>
          The minimum payment required each month is $40 (apart from the month of your first purchase), however you can choose to pay a higher amount if you wish to pay off your balance sooner.
        </div>
    },
    {
      question: 'What happens if I am not paying back my funds over time?',
      answer: 
        <div className={styles['answer-desc']}>
          On top of the monthly account service fee(of $7.95), a late fee of $5 may be charged if you do not make the minimum monthly repayment within 3 weeks of your due date, without prior contact with our customer support team, and it may have an effect on your credit score.
        </div>
    },
    {
      question: 'Up to 60 days to pay with no fee',
      answer: 
        <div className={styles['answer-desc']}>
          zipPay works like an account. For all purchases you make during a month you receive a statement with your closing balance on the 1st day of the following month. You have until the end of the month to pay off your balance for the account to remain fee free.<br />
          Here’s an example: If you make your first purchase on the 1st of March for $39, you will receive a statement on the 1st of April with the closing balance of $39 (unless you made more purchases in March) which you need to pay off until the 30th of April in order to not have to pay the account fee of $7.95.<br />
          Should you wish to carry your balance into May, you simply pay a small $7.95 monthly account fee. No balance - no fee!
        </div>
    },
    {
      question: 'Does zipPay view my credit report?',
      answer: 
        <div className={styles['answer-desc']}>
          When creating an account, we may perform identity and/or credit checks to verify the customer and their ability to manage payments. These checks are carried out in accordance with the Privacy Acknowledgment which you agree to and which can be accessed at the time you start the application.<br />
          Unfortunately it is outside of our hand to delete checks from your Veda file if your application got declined or after you close your account.
        </div>
    },
    {
      question: 'Why was my application unsuccessful?',
      answer: 
        <div className={styles['answer-desc']}>
          As you are reading this article, unfortunately we were unable to approve you for a Zip account at this stage.<br />
          Zip is a provider of credit and as such we need to check a few things to make sure we are being responsible.<br />
          We look at things like your credit score, income stability and if you can pay back the credit. We also check your identity via details provided, to make sure you are who you say you are. We aren&apos;t able to specify the exact reason as to why you have been declined, but we hope this gives you some idea on how we make a decision.<br />
          What we are really passionate about is not saying goodbye to you now, but instead working with you to improve your financial well-being so down the line you will be able to get approved for credit.<br />
          Here are a few tips on how to strengthen your credit applications in the future:<br />
          1. Consolidate and reduce your debt as much as possible<br />
          Reduce the number of your personal loans,credit cards, and avoid unnecessarily high credit card limits or short term high interest loans.<br />
          2. Manage your money on a regular basis<br />
          Get visibility of your spending by using a budgeting and money management tool like Pocketbook. Pocketbook automatically organises your spending into categories like clothes, groceries and fuel – giving you visibility of how your money is spent and how you can save.<br />
          In the future we are hoping to give customers the option to allow customers reapply at a later date.
        </div>
    }
  ]

  return (
    <>
      <Head>
        <title>
          Online Shopping Australia - Beauty & Health, Computers, Toys, - Deals & Discounts | CrazySales
        </title>
        <meta
          name="keywords"
          content="Online Shopping Australia, online shopping sites, Home Shopping, Shopping Deals, Best Deals, Home Garden, Pet Supplies, Sports Fitness, Bedding Bath, Buy Online, Discount Shopping, Toys Hobbies, Beauty Health, Crazy Sales, Computers, IT, Christmas, Electronics, Automotive, Tools, Appliances, Baby, CrazySales"
        ></meta>
        <meta
          name="description"
          content="CrazySales.com.au - Australia's #1 online shopping website! Cheap bargains &amp; discounts with the top online shopping Australia store! From furniture, to toys &amp; hobbies, to appliances &amp; pet supplies -- we've got it all, with free shipping on selected items!"
        ></meta>
      </Head>

      <DefaultLayout notStickyNavbar={true}>
        <div className={`${styles['page-zippay']} slider-wapper`}>
          <SliderBarLeft></SliderBarLeft>
          <SliderBarRight></SliderBarRight>

          <div className={styles["shop-info-box"]}>
            <ShopInfo hiddenContactus={true}></ShopInfo>
          </div>

          <div className={styles['content']}>
            <Image
              className={styles['title01-img']}
              src={`${imageDomain}/legal/what-is-zippay-title01.jpg`}
              alt={'title-img'}
              width={260}
              height={35}
            ></Image>
            
            <div className={styles['answer-desc']}>
              zipPay is a safe, simple and 100% interest-free digital wallet, offering you the ability to buy now and pay later over time - weekly, fortnightly or monthly. You don’t need to apply a credit card, you can simply apply for zipPay account on your mobile; the account is reusable, each account could be authorized up to $1,000. zipPay is owned by ASX listed zipMoney Limited (ASX: ZML), with all operations based in Sydney, Australia.
            </div>

            <Image
              className={styles['banner-img']}
              src={`${imageDomain}/legal/what-is-zippay-banner.jpg`}
              alt={'banner-img'}
              width={1152}
              height={246}
            ></Image>
            
            <Image
              className={styles['title02-img']}
              src={`${imageDomain}/legal/what-is-zippay-title02.jpg`}
              alt={'title-img'}
              width={378}
              height={43}
            ></Image>
            <div className={styles['answer-desc']}>
              Apply once and zipPay will give you a decision on the spot. If approved, you can use your account to purchase immediately. zipPay will pay Crazy Sales on your behalf and the purchase will go into standard ordering procedure immediately. You can simply pay back zipPay over time at a frequency to suit their lifestyle, and pay no interest ever.
            </div>

            <div className={styles['step-box']}>
              <Image
                className={styles['step-img']}
                src={`${imageDomain}/legal/what-is-zippay-step01.jpg`}
                alt={'title-img'}
                width={431}
                height={729}
              ></Image>
              <Image
                className={styles['step-img']}
                src={`${imageDomain}/legal/what-is-zippay-step02.jpg`}
                alt={'title-img'}
                width={431}
                height={729}
              ></Image>
            </div>

            <div className={styles['step-box']}>
              <Image
                className={styles['step-img']}
                src={`${imageDomain}/legal/what-is-zippay-step03.jpg`}
                alt={'title-img'}
                width={373}
                height={630}
              ></Image>
              <Image
                className={styles['step-img']}
                src={`${imageDomain}/legal/what-is-zippay-step04.jpg`}
                alt={'title-img'}
                width={373}
                height={630}
              ></Image>
            </div>

            <Image
              className={styles['title03-img']}
              src={`${imageDomain}/legal/what-is-zippay-title03.jpg`}
              alt={'title-img'}
              width={329}
              height={39}
            ></Image>
            
            <div className={styles['questions-box']}>
              {
                questionList.map((item: QuestionItem, index: number) => (
                  <div key={`faq-${index}`}>
                    <div className={styles['title']} id={`faq${index}`}>
                      Q{index + 1}. {item.question}
                    </div>
                    <div className={styles['answer']}>{item.answer}</div>
                    <div className={styles['back-top']}>
                      <UpOutlined style={{ color: '#D70B17', marginRight: '4px' }} onClick={backToTop} />
                      <span onClick={backToTop}>Back to Top</span>
                    </div>
                  </div>
                ))
              }
            </div>

            <div className={`${styles['answer-desc']} padding-top-24`}>
              The information on this page has been provided by zipPay. Crazy Sales is not in a position to verify the accuracy of the information or any claims made by third parties.
            </div>

            <div className={`${styles['ask-title']} padding-top-14`}>More Questions?</div>
            <div className={styles['answer-desc']}>
              <strong>Call</strong> : (02) 8294 2345
            </div>
            <div className={styles['answer-desc']}>
              <strong>Email</strong>: customercare@zipmoney.com.au
            </div>
            <div className={styles['answer-desc']}>
              <strong>For more information visit</strong>: https://www.zippay.com.au
            </div>
          </div>
        </div>
      </DefaultLayout>
    </>
  )
}